import { useState, useEffect, useCallback } from "react";
import { UserPlus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/lib/i18n";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

export const AdminMembers = ({ assoc, user }) => {
  const { t, lang } = useLang();
  const isAssocAdmin = user?.role === "association_admin" || user?.role === "super_admin";
  const [members, setMembers] = useState([]);
  const [mosques, setMosques] = useState([]);
  const [filter, setFilter] = useState(isAssocAdmin ? "all" : "");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "mosque_admin", mosque_ids: [] });

  useEffect(() => {
    api.get(`/associations/${assoc.slug}/mosques`).then(({ data }) => {
      const visible = isAssocAdmin ? data : data.filter((m) => (user?.mosque_ids || []).includes(m.id));
      setMosques(visible);
      if (!isAssocAdmin && visible.length > 0) setFilter((f) => f || visible[0].id);
    });
  }, [assoc.slug, isAssocAdmin, user]);

  const load = useCallback(() => {
    if (!filter) return;
    const url = filter === "all"
      ? `/associations/${assoc.slug}/members`
      : `/associations/${assoc.slug}/mosques/${filter}/members`;
    api.get(url).then(({ data }) => setMembers(data)).catch(() => setMembers([]));
  }, [assoc.slug, filter]);

  useEffect(() => { load(); }, [load]);

  const toggleMosque = (id) => setForm((f) => ({
    ...f,
    mosque_ids: f.mosque_ids.includes(id) ? f.mosque_ids.filter((x) => x !== id) : [...f.mosque_ids, id],
  }));

  const submit = async () => {
    try {
      await api.post(`/associations/${assoc.slug}/admins`, form);
      toast.success(t("saved"));
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "mosque_admin", mosque_ids: [] });
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading font-extrabold text-2xl tracking-tight">{t("members")}</h1>
        {isAssocAdmin && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-admin-btn" className="rounded-full active:scale-95">
                <UserPlus className="h-4 w-4 mr-2" /> {t("add_admin")}
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle className="font-heading">{t("add_admin")}</DialogTitle></DialogHeader>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label>{t("role")}</Label>
                  <Select value={form.role} onValueChange={(v) => setForm((f) => ({ ...f, role: v }))}>
                    <SelectTrigger data-testid="admin-form-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mosque_admin">{t("mosque_admin")}</SelectItem>
                      <SelectItem value="moderator">{t("moderator")}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5"><Label>{t("name")}</Label><Input data-testid="admin-form-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>{t("email")}</Label><Input type="email" data-testid="admin-form-email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>{t("password")}</Label><Input type="password" data-testid="admin-form-password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} /></div>
                {form.role === "mosque_admin" && (
                  <div className="space-y-2">
                    <Label>{t("assigned_mosques")}</Label>
                    {mosques.map((m) => (
                      <label key={m.id} className="flex items-center gap-2.5 text-sm cursor-pointer">
                        <Checkbox data-testid="admin-form-mosque-checkbox" checked={form.mosque_ids.includes(m.id)} onCheckedChange={() => toggleMosque(m.id)} />
                        {m.name}
                      </label>
                    ))}
                  </div>
                )}
                <Button data-testid="admin-form-submit" onClick={submit} disabled={!form.name || !form.email || form.password.length < 6 || (form.role === "mosque_admin" && form.mosque_ids.length === 0)} className="w-full rounded-full active:scale-95">
                  {t("create")}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="mb-5">
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="members-mosque-filter" className="w-full sm:w-72"><SelectValue /></SelectTrigger>
          <SelectContent>
            {isAssocAdmin && <SelectItem value="all">{t("all_members")}</SelectItem>}
            {mosques.map((m) => (
              <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {members.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-12 border border-dashed border-border rounded-lg" data-testid="admin-members-empty">
          {t("no_members_yet")}
        </p>
      ) : (
        <div className="border border-border rounded-lg overflow-hidden" data-testid="admin-members-table">
          {members.map((m) => (
            <div key={m.id} className="flex items-center gap-3 px-4 py-3 border-b border-border last:border-0">
              <div className="min-w-0 flex-1">
                <p className="font-medium text-sm">{m.name}</p>
                <p className="text-xs text-muted-foreground">{m.email}</p>
              </div>
              <Badge variant={m.role === "member" ? "secondary" : "default"} className="text-[10px]">{t(m.role)}</Badge>
              <span className="text-xs font-mono-data text-muted-foreground hidden sm:inline">
                {m.joined_at ? new Date(m.joined_at).toLocaleDateString(lang === "fr" ? "fr-FR" : "en-US") : ""}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
